import type { WallpaperConfig } from '../config/schema';
import { computeDotGrid, resolveSafeAreas } from '../lib/grid';
import { Dot } from '../components/Dot';
import { lifeCellState, lifeGridDimensions, parseISODate, unitsLived } from '../lib/dates';

interface Props {
  config: WallpaperConfig;
  today: Date;
  titleHeight: number;
}

export function LifeGrid({ config, today, titleHeight }: Props) {
  const { life, device, padding, colors, shape, highlightToday, showLabels } = config;
  const { rows, cols } = lifeGridDimensions(life.unit, life.lifeExpectancy);

  const labelGutter = showLabels && life.grouping === 'decade' ? 60 : 0;

  const { clockReserved, bottomReserved } = resolveSafeAreas(padding, device);

  const layout = computeDotGrid({
    width: device.width,
    height: device.height,
    paddingH: padding.horizontal,
    paddingV: padding.vertical,
    rows,
    cols,
    topReserved: titleHeight + clockReserved,
    bottomReserved,
    leftReserved: labelGutter,
    groupEvery: life.grouping === 'decade' ? 10 : 0,
    groupGapRatio: 0.6,
  });

  const lived = unitsLived(parseISODate(life.birthDate), today, life.unit);

  const dots = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      const state = lifeCellState(idx, lived, highlightToday);
      const color =
        state === 'past' ? colors.filled : state === 'today' ? colors.today : colors.empty;
      const { cx, cy } = layout.cellPosition(r, c);
      dots.push(
        <Dot key={`${r}-${c}`} cx={cx} cy={cy} r={layout.dotRadius} color={color} shape={shape} />,
      );
    }
  }

  const labels = [];
  if (showLabels && life.grouping === 'decade') {
    for (let decade = 10; decade <= rows; decade += 10) {
      const { cy } = layout.cellPosition(decade - 1, 0);
      labels.push(
        <text
          key={decade}
          x={layout.originX - layout.pitchX / 2 - layout.dotRadius * 0.6}
          y={cy + layout.dotRadius * 0.5}
          fill={colors.text}
          fontFamily="ui-sans-serif, system-ui, -apple-system"
          fontSize={layout.pitch * 1.1}
          textAnchor="end"
          opacity={0.5}
        >
          {decade}
        </text>,
      );
    }
  }

  return (
    <g data-mode="life">
      {dots}
      {labels}
    </g>
  );
}
